/**
 * UploadMaestroModal - Multi-step Excel import modal for Maestro financial data
 * 
 * Steps:
 *   1. File Select (drag-and-drop or browse)
 *   2. Validating (parsing Excel)
 *   3. Column Review (green/red validation grid)
 *   4. Confirm (summary + warning)
 *   5. Uploading (progress bar)
 * 
 * Stores data in the forecasting.maestro_import table.
 */

import React, { useState, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import * as XLSX from 'xlsx';

// All known database columns for maestro_import (forecasting schema)
const KNOWN_COLUMNS = new Set([
  // Text fields
  'project', 'prefix', 'phase', 'activity', 'cost_type', 'group_col',
  // Financial columns
  'orig_bud', 'actual_plus_accrued', 'production', 'production_q', 'accrued',
  'bud_bal', 'total_and_po', 'rev_qty_to_fulfill', 'mod_qty_to_fulfill',
  'po_bal_q', 'projection', 'orig_qty_to_fulfill', 'billing_q', 'po_inv',
  'total_and_po_q', 'projection_q', 'selling_bud_modif', 'tot_bud_bal',
  'contingency', 'to_complete_q', 'to_complete', 'selling_bud_modif_q',
  'contingency_q', 'tot_bud_bal_q', 'revised_selling_bud_q', 'revised_selling_bud',
  'orig_selling_bud_q', 'orig_selling_bud', 'po_total_q', 'revised_bud_q',
  'bud_modif_q', 'po_inv_q', 'actual_q', 'bud_bal_q', 'billing', 'accrued_q',
  'orig_bud_q', 'actual_plus_accrued_q', 'bud_modif', 'revised_bud', 'actual',
  'po_total', 'po_bal', 'comm_costs',
  // Percentage columns
  'pct_budget_spent', 'pct_budget_spent_tot_q', 'pct_budget_spent_tot', 'pct_budget_spent_q'
]);

// Text fields that should NOT be converted to numbers
const TEXT_FIELDS = new Set(['project', 'prefix', 'phase', 'activity', 'cost_type', 'group_col']);

// Aliases for Excel headers whose natural db name conflicts with SQL reserved words
// or needs explicit remapping. Applied after headerToDbColumn transformation.
// e.g. Excel 'Group' -> headerToDbColumn -> 'group' (SQL reserved) -> alias -> 'group_col'
const COLUMN_ALIASES = { 'group': 'group_col' };

/**
 * Convert Excel column header to database column name.
 * e.g., "Actual+Accrued" -> "actual_plus_accrued"
 *       "%Budget Spent" -> "pct_budget_spent"
 *       "Production(Q)" -> "production_q"
 */
const headerToDbColumn = (header) => {
  return header
    .toLowerCase()
    .replace(/%/g, 'pct_')               // % -> pct_ prefix
    .replace(/\(q\)/gi, '_q')            // (Q) -> _q suffix
    .replace(/\+/g, '_plus_')            // + -> _plus_
    .replace(/&/g, '_and_')              // & -> _and_
    .replace(/\s+/g, '_')               // spaces -> underscores
    .replace(/[().]/g, '')               // remove remaining parens/dots
    .replace(/_+/g, '_')                 // collapse multiple underscores
    .replace(/^_|_$/g, '');              // trim leading/trailing underscores
};

/**
 * Parse numeric value safely
 */
const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    if (!isFinite(value)) return null;
    return Math.round(value * 100) / 100;
  }
  const cleaned = String(value).replace(/[$,\s]/g, '');
  const parsed = parseFloat(cleaned);
  if (isNaN(parsed) || !isFinite(parsed)) return null;
  return Math.round(parsed * 100) / 100;
};

const UploadMaestroModal = React.memo(({ isOpen, onClose, onUploadSuccess, onUploadError, projectId }) => {
  const { instance, accounts } = useMsal();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('select'); // 'select', 'validate', 'review', 'confirm', 'uploading'
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [columnMatches, setColumnMatches] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [cutoffDate, setCutoffDate] = useState('');
  const [importNote, setImportNote] = useState('');

  // Drag handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'
      ];
      if (validTypes.includes(file.type) ||
          file.name.endsWith('.xlsx') ||
          file.name.endsWith('.xls') ||
          file.name.endsWith('.csv')) {
        setSelectedFile(file);
        parseExcelFile(file);
      } else {
        setValidationError('Please select an Excel file (.xlsx, .xls, or .csv)');
      }
    }
  }, []);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      parseExcelFile(file);
    }
    // Reset file input so re-selecting the same file triggers onChange
    e.target.value = '';
  }, []);

  /**
   * Parse the Excel file, detect columns, and validate
   */
  const parseExcelFile = async (file) => {
    setLoading(true);
    setValidationError('');
    setStep('validate');

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Parse with header row
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        setValidationError('No data found in Excel file. Please check that row 1 contains headers.');
        setLoading(false);
        return;
      }

      // Get all unique headers from the data
      const headers = [...new Set(jsonData.flatMap(row => Object.keys(row)))];

      // Match columns
      const matches = {};
      headers.forEach(header => {
        const raw = headerToDbColumn(header);
        const dbCol = COLUMN_ALIASES[raw] || raw;
        const found = KNOWN_COLUMNS.has(dbCol);
        matches[header] = {
          found,
          excelColumn: header,
          dbColumn: dbCol
        };
      });

      // Map rows to database columns (only matched columns)
      const mappedRows = jsonData.map(row => {
        const mapped = {};
        Object.entries(row).forEach(([key, value]) => {
          const raw = headerToDbColumn(key);
          const dbKey = COLUMN_ALIASES[raw] || raw;
          if (KNOWN_COLUMNS.has(dbKey)) {
            if (TEXT_FIELDS.has(dbKey)) {
              mapped[dbKey] = String(value || '');
            } else {
              mapped[dbKey] = parseNumericValue(value);
            }
          }
        });
        return mapped;
      });

      setColumnMatches(matches);
      setParsedData(mappedRows);
      setStep('review');
      setLoading(false);
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      setValidationError(`Failed to parse Excel file: ${error.message}`);
      setLoading(false);
    }
  };

  /**
   * Upload the parsed data to the backend
   */
  const handleUpload = async () => {
    if (!parsedData || parsedData.length === 0) {
      onUploadError?.('No data to upload');
      return;
    }

    setStep('uploading');
    setLoading(true);
    setUploadProgress(10);
    setUploadStatus('Uploading data to server...');

    try {
      if (!accounts || accounts.length === 0) {
        throw new Error('No authenticated user found');
      }

      setUploadProgress(30);

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/maestro-reports/${projectId}/import`,
        {
          rows: parsedData,
          cutoffDate: cutoffDate || null,
          notes: importNote.trim() || null,
        }
      );

      setUploadProgress(100);
      setUploadStatus('Import complete!');

      if (response.data.success) {
        setTimeout(() => {
          onUploadSuccess?.({
            inserted: response.data.data.inserted,
            total: response.data.data.total,
            errors: response.data.data.errors
          });
          resetModal();
        }, 800);
      } else {
        throw new Error(response.data.error || 'Import failed');
      }
    } catch (error) {
      console.error('Error uploading data:', error);
      setUploadStatus('Upload failed');
      setUploadProgress(0);
      onUploadError?.(error.response?.data?.error || error.message || 'Failed to upload data');
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setSelectedFile(null);
    setParsedData(null);
    setColumnMatches(null);
    setValidationError('');
    setStep('select');
    setUploadProgress(0);
    setUploadStatus('');
    setDragActive(false);
    setLoading(false);
    setCutoffDate('');
    setImportNote('');
    onClose();
  };

  const handleBack = () => {
    if (step === 'confirm') {
      setStep('review');
    } else {
      setSelectedFile(null);
      setParsedData(null);
      setColumnMatches(null);
      setValidationError('');
      setStep('select');
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  // Compute match stats for display
  const renderOptionalMeta = () => (
    <div style={{
      backgroundColor: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      padding: '1rem',
      marginBottom: '1rem',
    }}>
      <p style={{ margin: '0 0 0.75rem 0', color: '#334155', fontSize: '0.9rem', lineHeight: 1.5 }}>
        <strong>Cut-off date and note</strong> help you recognize this import later.
        Both are optional — leave them blank and the upload still goes through.
      </p>
      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.35rem' }}>
        Cut-off date <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional)</span>
      </label>
      <input
        type="date"
        value={cutoffDate}
        onChange={(e) => setCutoffDate(e.target.value)}
        style={{
          width: '100%', maxWidth: '220px', padding: '0.5rem 0.65rem',
          border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem',
          marginBottom: '0.85rem', boxSizing: 'border-box',
        }}
      />
      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.35rem' }}>
        Note <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional)</span>
      </label>
      <textarea
        value={importNote}
        onChange={(e) => setImportNote(e.target.value)}
        maxLength={500}
        rows={2}
        placeholder="e.g. September month-end, before the extra"
        style={{
          width: '100%', padding: '0.5rem 0.65rem', border: '1px solid #d1d5db',
          borderRadius: '6px', fontSize: '0.95rem', resize: 'vertical',
          boxSizing: 'border-box', fontFamily: 'inherit',
        }}
      />
    </div>
  );

  const matchedCount = columnMatches ? Object.values(columnMatches).filter(m => m.found).length : 0;
  const unmatchedCount = columnMatches ? Object.values(columnMatches).filter(m => !m.found).length : 0;
  const totalHeaders = columnMatches ? Object.keys(columnMatches).length : 0;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: (step === 'review' || step === 'confirm') ? '800px' : '500px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            Upload Maestro Data
            {step === 'validate' && ' - Validating...'}
            {step === 'review' && ' - Column Review'}
            {step === 'confirm' && ' - Confirm Import'}
            {step === 'uploading' && ' - Uploading...'}
          </h3>
          <button
            onClick={resetModal}
            disabled={loading && step === 'uploading'}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: (loading && step === 'uploading') ? 'not-allowed' : 'pointer',
              color: '#666',
              padding: '0.25rem',
              opacity: (loading && step === 'uploading') ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        {/* Step 1: File Selection */}
        {step === 'select' && (
          <>
            <div
              style={{
                border: `2px dashed ${dragActive ? '#007bff' : '#ddd'}`,
                borderRadius: '8px',
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: dragActive ? '#f8f9ff' : '#f8f9fa',
                transition: 'all 0.2s ease',
                marginBottom: '1.5rem'
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>

              {selectedFile ? (
                <div>
                  <p style={{ margin: '0 0 1rem 0', color: '#28a745', fontWeight: 'bold' }}>
                    File selected: {selectedFile.name}
                  </p>
                  <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                    Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div>
                  <p style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>
                    {dragActive ? 'Drop your Excel file here' : 'Drag and drop your Maestro Excel file here'}
                  </p>
                  <p style={{ margin: '0 0 1rem 0', color: '#666' }}>or</p>
                  <button
                    onClick={handleBrowseClick}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Browse Files
                  </button>
                  <p style={{ margin: '1rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                    Supports .xlsx, .xls, and .csv files
                  </p>
                </div>
              )}
            </div>

            {renderOptionalMeta()}

            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '1rem',
              borderRadius: '4px',
              fontSize: '0.9rem',
              color: '#1976d2'
            }}>
              <strong>Requirements:</strong>
              <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                <li>Excel file exported from Maestro with financial data</li>
                <li>Row 1 must contain column headers (e.g., "Orig Bud", "Actual+Accrued", "Production(Q)")</li>
                <li>Columns are automatically matched to database fields</li>
                <li>Unrecognized columns will be skipped</li>
                <li>This import will be saved as a new snapshot — previous imports are retained</li>
              </ul>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </>
        )}

        {/* Step 2: Validation in progress */}
        {step === 'validate' && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            {validationError ? (
              <>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                </div>
                <div style={{
                  backgroundColor: '#ffebee',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                  textAlign: 'left'
                }}>
                  <p style={{
                    margin: '0 0 1rem 0',
                    fontWeight: 'bold',
                    color: '#c62828',
                    fontSize: '1.1rem'
                  }}>
                    Validation Failed
                  </p>
                  <p style={{ margin: 0, color: '#b71c1c', lineHeight: '1.5' }}>
                    {validationError}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setStep('select');
                    setSelectedFile(null);
                    setValidationError('');
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Select Different File
                </button>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{
                    display: 'inline-block',
                    width: '40px',
                    height: '40px',
                    border: '3px solid #e9ecef',
                    borderTopColor: '#007bff',
                    borderRadius: '50%',
                    animation: 'maestroSpin 1s linear infinite'
                  }} />
                </div>
                <p style={{ color: '#666', marginBottom: '0.5rem' }}>
                  Parsing Excel file...
                </p>
                <p style={{ color: '#999', fontSize: '0.85rem' }}>
                  Reading headers and data rows
                </p>
              </>
            )}
          </div>
        )}

        {/* Step 3: Column Review */}
        {step === 'review' && columnMatches && (
          <>
            <div style={{
              backgroundColor: '#e8f5e9',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem'
            }}>
              <p style={{ margin: 0, fontWeight: 'bold', color: '#2e7d32' }}>
                Found {parsedData?.length || 0} data rows with {totalHeaders} columns
              </p>
              <p style={{ margin: '0.25rem 0 0 0', color: '#388e3c', fontSize: '0.9rem' }}>
                {matchedCount} columns matched | {unmatchedCount > 0 ? `${unmatchedCount} unrecognized (will be skipped)` : 'All columns recognized'}
              </p>
            </div>

            <div style={{
              marginBottom: '1.5rem',
              maxHeight: '400px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '1rem'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>
                Column Validation Results
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '0.5rem'
              }}>
                {Object.keys(columnMatches).map((col) => {
                  const match = columnMatches[col];
                  return (
                    <div
                      key={col}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: match.found ? '#e8f5e9' : '#ffebee',
                        borderRadius: '4px',
                        fontSize: '0.85rem'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1rem' }}>
                          {match.found ? '\u2713' : '\u2717'}
                        </span>
                        <span style={{
                          color: match.found ? '#2e7d32' : '#c62828',
                          wordBreak: 'break-word'
                        }}>
                          {col}
                        </span>
                      </div>
                      <span style={{
                        color: '#888',
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        marginLeft: '0.5rem',
                        flexShrink: 0
                      }}>
                        {match.found ? match.dbColumn : 'skipped'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleBack}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Back
              </button>
              <button
                onClick={() => setStep('confirm')}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {/* Step 4: Confirm */}
        {step === 'confirm' && (
          <>
            <div style={{
              backgroundColor: '#e8f5e9',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              <p style={{ margin: 0, fontWeight: 'bold', color: '#2e7d32', fontSize: '1.05rem' }}>
                Ready to import {parsedData?.length || 0} rows
              </p>
              <p style={{ margin: '0.25rem 0 0 0', color: '#388e3c', fontSize: '0.9rem' }}>
                {matchedCount} of {totalHeaders} columns will be imported
              </p>
            </div>

            <div style={{
              backgroundColor: '#fff3cd',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.9rem',
              color: '#856404'
            }}>
              <strong>Note:</strong> This import will be saved as a new snapshot. All previous imports are retained and accessible via the Import History timeline.
            </div>

            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              color: '#495057'
            }}>
              <strong>File:</strong> {selectedFile?.name}<br />
              <strong>Rows:</strong> {parsedData?.length || 0}<br />
              <strong>Matched Columns:</strong> {matchedCount}{unmatchedCount > 0 ? ` (${unmatchedCount} skipped)` : ''}
            </div>

            {renderOptionalMeta()}

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleBack}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Back
              </button>
              <button
                onClick={handleUpload}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Confirm & Import {parsedData?.length || 0} Rows
              </button>
            </div>
          </>
        )}

        {/* Step 5: Upload Progress */}
        {step === 'uploading' && (
          <div style={{ padding: '1rem 0' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ fontSize: '0.9rem', color: '#666' }}>{uploadStatus}</span>
                <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>{uploadProgress}%</span>
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#e9ecef',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${uploadProgress}%`,
                  height: '100%',
                  backgroundColor: uploadProgress === 100 ? '#28a745' : '#007bff',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
            {uploadProgress === 100 && (
              <p style={{ textAlign: 'center', color: '#28a745', fontWeight: 'bold' }}>
                Import complete! Closing...
              </p>
            )}
          </div>
        )}
      </div>

      {/* Spinner animation */}
      <style>{`
        @keyframes maestroSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
});

UploadMaestroModal.displayName = 'UploadMaestroModal';

export default UploadMaestroModal;
